import { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle, Info, XCircle } from 'lucide-react';

interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
}

function Alert({ variant = 'info', title, children, className, ...props }: AlertProps) {
  const variants = {
    info: {
      container: 'bg-blue-950/50 border-blue-800/50 text-blue-200',
      icon: Info,
      iconColor: 'text-blue-400',
    },
    success: {
      container: 'bg-green-950/50 border-green-800/50 text-green-200',
      icon: CheckCircle,
      iconColor: 'text-green-400',
    },
    warning: {
      container: 'bg-yellow-950/50 border-yellow-800/50 text-yellow-200',
      icon: AlertCircle,
      iconColor: 'text-yellow-400',
    },
    error: {
      container: 'bg-red-950/50 border-red-800/50 text-red-200',
      icon: XCircle,
      iconColor: 'text-red-400',
    },
  };

  const { container, icon: Icon, iconColor } = variants[variant];

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 p-4 border rounded-lg backdrop-blur-sm',
        container,
        className
      )}
      {...props}
    >
      <Icon className={cn('w-5 h-5 mt-0.5 flex-shrink-0', iconColor)} />
      <div className="flex-1">
        {title && <div className="font-semibold mb-1 uppercase tracking-wider text-sm">{title}</div>}
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
}

export default Alert;
